import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DataService } from '@vendure/admin-ui/core';
import { gql } from 'graphql-tag';
import { catchError, distinctUntilChanged, filter, map, of, retry, switchMap, timer } from 'rxjs';

const GET_TAWK_CHAT_CONFIG = gql`
    query GetTawkChatConfig {
        tawkChatConfig {
            embedChatId
            hmacHash
            email
        }
    }
`;

@Injectable({ providedIn: 'root' })
export class TawkChatService {
    constructor(
        @Inject(DOCUMENT) private document: Document,
        private dataService: DataService,
    ) {}

    init(): void {
        this.dataService.auth.currentUser().stream$.pipe(
            map(({ me }) => me?.identifier ?? null),
            distinctUntilChanged(),
            filter((identifier): identifier is string => identifier != null),
            switchMap(() =>
                this.dataService.query(GET_TAWK_CHAT_CONFIG)
                    .mapSingle((data: any) => data.tawkChatConfig)
                    .pipe(
                        // Retry transient failures (Apollo cold-start, auth cookie not
                        // yet propagated after login — common for multi-channel seller
                        // flows where currentUser resolves before the next request is
                        // authenticated). Back off 500/1000/1500ms; give up after that.
                        retry({ count: 3, delay: (_err, attempt) => timer(500 * (attempt + 1)) }),
                        catchError(() => of(null)),
                    ),
            ),
        ).subscribe(config => {
            if (!config?.embedChatId) return;
            this.initOrUpdateWidget(config.embedChatId, config.email, config.hmacHash);
        });
    }

    private initOrUpdateWidget(embedChatId: string, email: string, hmacHash: string): void {
        const win = this.document.defaultView as any;
        if (!win) return;
        const normalizedEmbedChatId = this.normalizeEmbedChatId(embedChatId);
        if (!normalizedEmbedChatId) {
            console.warn('[Tawk] Invalid embedChatId format. Expected "<propertyId>/<widgetId>" or a full embed.tawk.to URL.');
            return;
        }

        // If Tawk is already loaded, just update the visitor attributes
        if (win.Tawk_API?.setAttributes) {
            this.setVisitorAttributes(win, email, hmacHash);
            return;
        }

        // Script element exists but Tawk hasn't finished loading yet (e.g. fast
        // re-login or hot navigation). Register onLoad on the stub so identity
        // attaches once the remote script initializes, instead of bailing out.
        win.Tawk_API = win.Tawk_API || {};
        win.Tawk_API.onLoad = () => {
            this.setVisitorAttributes(win, email, hmacHash);
        };

        if (this.document.getElementById('tawkio-widget-script')) return;

        win.Tawk_LoadStart = new Date();

        const script = this.document.createElement('script');
        script.id = 'tawkio-widget-script';
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://embed.tawk.to/${normalizedEmbedChatId}`;
        script.charset = 'UTF-8';
        script.setAttribute('crossorigin', 'anonymous');
        script.onerror = () => {
            console.error('[Tawk] Widget script failed to load:', script.src);
        };

        const firstScript = this.document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            this.document.head.appendChild(script);
        }
    }

    private setVisitorAttributes(win: any, email: string, hmacHash: string): void {
        if (!win?.Tawk_API?.setAttributes) return;
        win.Tawk_API.setAttributes({ email, hash: hmacHash }, (error: unknown) => {
            if (error) {
                console.error('[Tawk] setAttributes failed:', error);
            }
        });
    }

    private normalizeEmbedChatId(rawEmbedChatId: string): string | null {
        const raw = (rawEmbedChatId ?? '').trim();
        if (!raw) {
            return null;
        }
        const withoutOrigin = raw
            .replace(/^https?:\/\/embed\.tawk\.to\//i, '')
            .replace(/^embed\.tawk\.to\//i, '');
        const normalized = withoutOrigin.split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
        if (!normalized.includes('/')) {
            return null;
        }
        return normalized;
    }
}
